import type {
    AttributeValue,
    Bundle,
    BundleTemplate,
    ComposableOptionsBase,
    GenericObject,
    ProductAttrsDefaultValuesMap,
    ProductAttrsDefaultValuesModelMap
} from '~/types';
import {
    getSingleParamValue
} from 'x1-base/utils';
import { isObject as lo_isObject } from 'es-toolkit/compat';
import type { WatchStopHandle } from 'vue';

export interface UseProductCustomBundleUtilsOptions extends ComposableOptionsBase {
    autoInit?: boolean;
    disabled?: boolean;
}
export function useProductCustomBundleUtils(options: Partial<UseProductCustomBundleUtilsOptions> = {}) {
    const route = useRoute();
    const savedBundles = ref<Bundle[]>([]);
    const selectedBundle = ref<Nullable<Bundle>>(null);
    const selectedBundleUid = ref<Nullable<string>>(null);
    const attrsDefaultValuesMap = ref<Nullable<ProductAttrsDefaultValuesMap>>(null);
    const attrsDefaultValuesModelMap = computed<Nullable<ProductAttrsDefaultValuesModelMap>>(() => {
        if (!attrsDefaultValuesMap.value || !selectedBundleUid.value) return null;
        return  attrsDefaultValuesMap.value.get(selectedBundleUid.value) || null;
    });
    const bundleViewMode = ref<'customize' | 'saved'>('customize');
    const bundleTemplateIsModified = ref<boolean>(false);
    const bundleTemplateResetKey = ref<number>(0);

    let unWatchBundles: WatchStopHandle;
    let unWatchBundleViewMode: WatchStopHandle;

    async function toggleQueryBundleUid(uid?: Nullable<string>): Promise<void> {
        const obj: Parameters<typeof navigateTo>[0] = { path: route.path, replace: true };
        const query = Object.assign({}, route.query);

        if (uid) {
            query.bundle = uid;
        } else {
            delete query.bundle;
        }

        await navigateTo({ ...obj, query });
    }

    function selectBundleByUid(uid: Nullable<string>): void {
        const bundle = savedBundles.value.find(bundle => bundle.uid === uid) || null;
        selectedBundle.value = bundle;
        selectedBundleUid.value = bundle?.uid || null;
    }

    function clearBundleSelection(): void {
        selectedBundle.value = null;
        selectedBundleUid.value = null;
    }

    function processBundles(bundleTemplate?: Nullable<BundleTemplate>): void {
        // Build optionValueUid → attributeUid lookup from bundle template
        const attrsValsLookup: GenericObject<string> = {};
        bundleTemplate?.lines.forEach((line) => {
            line.optionsData.forEach((od) => {
                od.values.forEach((v) => {
                    attrsValsLookup[v.uid] = od.attributeUid;
                });
            });
        });

        const defaultValuesMap: ProductAttrsDefaultValuesMap = new Map();

        savedBundles.value.forEach((bundle) => {
            const bundleMapItem: ProductAttrsDefaultValuesModelMap = {};

            bundle.components.forEach((component) => {
                const { lineUid } = component;
                if (!lineUid) return;

                // Option lines: use component.option directly
                if (lo_isObject(component.option)) {
                    const { uid, attributeUid } = component.option;
                    if (uid && attributeUid) {
                        bundleMapItem[lineUid] = {
                            ...(bundleMapItem[lineUid] || {}),
                            [attributeUid]: uid
                        };
                    }
                }

                // SKU lines: derive attribute values from sku.options
                if (component.sku?.options) {
                    const options = component.sku.options as AttributeValue[];
                    options.forEach((opt) => {
                        const attrUid = opt.attributeUid || opt.attribute?.uid || attrsValsLookup[opt.uid];
                        if (opt.uid && attrUid) {
                            bundleMapItem[lineUid] = {
                                ...(bundleMapItem[lineUid] || {}),
                                [attrUid]: opt.uid
                            };
                        }
                    });
                }
            });

            defaultValuesMap.set(bundle.uid, bundleMapItem);
        });

        attrsDefaultValuesMap.value = defaultValuesMap;
    }

    async function appendBundle(bundle: Bundle): Promise<void> {
        savedBundles.value.push(bundle);
        selectBundleByUid(bundle.uid);
        processBundles();
        await toggleQueryBundleUid(bundle.uid);
    }

    function init(defaultBundleUid?: Nullable<string>): void {
        const bundleUidFromQuery = getSingleParamValue(route.query.bundle);

        if (bundleUidFromQuery) {
            selectBundleByUid(bundleUidFromQuery);
        } else if (typeof defaultBundleUid === 'string' && defaultBundleUid) {
            selectBundleByUid(defaultBundleUid);
        }

        if (savedBundles.value.length) {
            processBundles();
        }
    }

    if (!options.disabled) {
        if (options.autoInit) {
            unWatchBundles = watch(
                savedBundles,
                init,
                {
                    immediate: true
                }
            );
        }

        unWatchBundleViewMode = watch(
            bundleViewMode,
            (mode) => {
                if (mode === 'customize') {
                    toggleQueryBundleUid();
                }
            }
        );
    }

    onBeforeUnmount(() => {
        unWatchBundles?.();
        unWatchBundleViewMode?.();
    });

    return {
        init,
        processBundles,
        savedBundles,
        selectedBundle,
        selectedBundleUid,
        toggleQueryBundleUid,
        selectBundleByUid,
        clearBundleSelection,
        attrsDefaultValuesMap,
        attrsDefaultValuesModelMap,
        bundleViewMode,
        bundleTemplateIsModified,
        bundleTemplateResetKey,
        appendBundle
    };
}
